import React from "react";
import { Workflow } from "lucide-react";
import { referrals } from "../../data/marketplaceData";

/* ─────────────────────────────────────────────────────────────
   MarketingReferralTable · Phase 5 Homepage
   ------------------------------------------------------------
   Marketing-safe presentation of the SAME referral data shape
   used by the production `ReferralTracking` component. This
   wrapper reads the identical dataset (../../data/marketplaceData)
   but OMITS commercially sensitive fixture values that must not
   be presented as public performance proof:
     • the "£128.4K MTD" chip
     • the "Revenue" column
   Everything else (Client / Property / Partner / Service /
   Status / Progress) is preserved verbatim from the production
   table, including the same status vocabulary.

   The authenticated `ReferralTracking` primitive at
   /components/marketplace/ReferralTracking.jsx is untouched.
   ───────────────────────────────────────────────────────────── */

const STATUS = {
  active:    { chip: "bg-indigo-50 text-indigo-700 border-indigo-200",  dot: "bg-indigo-500", label: "Active",    color: "from-indigo-500 to-indigo-400" },
  completed: { chip: "bg-slate-100 text-slate-700 border-slate-200",    dot: "bg-slate-400",  label: "Completed", color: "from-slate-500 to-slate-400" },
  pending:   { chip: "bg-amber-50 text-amber-700 border-amber-200",     dot: "bg-amber-500",  label: "Pending",   color: "from-amber-500 to-amber-400" },
  stalled:   { chip: "bg-rose-50 text-rose-700 border-rose-200",        dot: "bg-rose-500",   label: "Stalled",   color: "from-rose-500 to-rose-400" },
};

export const MarketingReferralTable = () => (
  <section
    className="rounded-2xl border border-slate-200 bg-white cf-shadow overflow-hidden"
    data-testid="marketing-referral-table"
  >
    <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center">
        <Workflow size={14} />
      </div>
      <div>
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Live</p>
        <h3 className="font-display text-[18px] font-bold text-slate-900">Referral tracking</h3>
      </div>
    </div>

    <div className="overflow-x-auto cf-scroll">
      <table className="w-full text-left min-w-[820px]">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold border-b border-slate-100">
            <th className="px-5 py-3 font-semibold">Client</th>
            <th className="px-3 py-3 font-semibold">Property</th>
            <th className="px-3 py-3 font-semibold">Partner</th>
            <th className="px-3 py-3 font-semibold">Service</th>
            <th className="px-3 py-3 font-semibold">Date</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-3 py-3 font-semibold w-[180px]">Progress</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((r) => {
            const s = STATUS[r.status];
            return (
              <tr
                key={r.id}
                data-testid={`marketing-referral-${r.id}`}
                className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/60 transition-colors"
              >
                <td className="px-5 py-3.5"><p className="text-[13px] font-semibold text-slate-900">{r.client}</p></td>
                <td className="px-3 py-3.5"><p className="text-[12.5px] text-slate-700 truncate max-w-[200px]">{r.property}</p></td>
                <td className="px-3 py-3.5"><p className="text-[12.5px] text-slate-900 font-medium truncate">{r.partner}</p></td>
                <td className="px-3 py-3.5">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                    {r.category}
                  </span>
                </td>
                <td className="px-3 py-3.5"><p className="text-[12px] text-slate-700 font-mono-cf">{r.date}</p></td>
                <td className="px-3 py-3.5">
                  <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${s.chip}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /> {s.label}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full bg-gradient-to-r ${s.color}`} style={{ width: `${r.progress}%` }} />
                    </div>
                    <span className="text-[10.5px] font-mono-cf text-slate-600 w-9 text-right">{r.progress}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </section>
);

export default MarketingReferralTable;
